import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import {
  noop,
  VALIDATIONS,
  USER_CONFIG_STORAGE_KEY,
} from "@client/shared/constants";
import { getUserData, validate as validator } from "@client/utils";
import CreateBroadcastView from "./view";
import Service from "../service";
import { AppService } from "@client/shared/services";
import { Alert } from "@client/shared/components";
import Typography from "@mui/material/Typography";
import { AppContext } from "@client/shared/contexts";

import {
  BroadcastPanel,
  ConfirmDetailsPanel,
  EmailUpdatesPanel,
  RecipientsPanel,
  ReleaseDatePanel,
  AudioRecordPanel,
  ProgressPanel,
} from "./panels";

let isFormChanged = false;

const defaultState = {
  broadcastTitle: "",
  callerIdName: "",
  callerIdNumber: "",
  overrideCallerId: false,
  isAllRecipients: false,
  recipientSites: [],
  recipientDepartments: [],
  recipientRoles: [],
  recipientSelectedContacts: [],
  recipients: [],
  notifyRecipient: true,
  immediateRelease: false,
  releaseDay: "",
  releaseMonth: null,
  releaseYear: "",
  releaseTime: "",
  releaseMeridiem: "AM",
  bbc: [],
  recordingType: 1,
  selectedVoicemail: {},
  uploadedVoicemail: null,
  recordedVoicemail: null,
  recordedVoicemailTitle: "",
  callToRecordVoicemail: null,
  callToRecordVoicemailNumber: null,
  callToRecordVoicemailTitle: "",
  includeSelfInBroadcast: true,
  panelIndex: 0,
  isSaving: false,
  isCancelling: false,
  broadcastComplete: false,
  alert: {},
  errors: {
    broadcastTitle: " ",
    callerIdName: " ",
  },
};

const CreateBroadcast = ({
  siteList = [],
  departmentList = [],
  userRoleList = [],
  individualContactList = [],
  extensionList = [],
  phoneNumberList = [],
  voicemailList = [],
  countryList = [],
  accountInfo = {},
  rowBeingEdited = null,
  showDetails = false,
  roleAccess = {},
  onClose = noop,
  handleDelete = noop,
  handleJwksResponse = noop,
}) => {
  const userData = (getUserData() || {}).user || {};
  const { appData } = useContext(AppContext);
  const [state, setState] = useState({
    ...defaultState,
    panelIndex: showDetails ? 5 : defaultState.panelIndex,
    recordingType: roleAccess?.uploadMedia
      ? 1
      : roleAccess?.accessMedia
      ? 3
      : 0,
  });

  const patchPreference = async (preference = false) => {
    setState((prevState) => ({
      ...prevState,
      alert: {
        ...prevState.alert,
        preference,
      },
    }));
    const { error } = await Service.setUserPreference(userData.id, {
      [state.alert.preferenceName]: preference,
    });
    if (error) {
      toast.error(
        Array.isArray(error) ? error[0].message : error.message || error
      );
      setState((prevState) => ({
        ...prevState,
        alert: {
          ...prevState.alert,
          preference: !preference,
        },
      }));
    } else {
      localStorage.setItem(
        USER_CONFIG_STORAGE_KEY,
        JSON.stringify({
          user: { ...userData, [state.alert.preferenceName]: preference },
        })
      );
    }
  };

  const getOldestVoicemail = async () => {
    let voicemails = [...voicemailList];
    const query = "?filter[order]=releaseDate ASC";
    const { data: broadcasts } = await Service.getBroadcasts(query);
    if (broadcasts?.broadcastVoicemail && voicemails.length < 100) {
      const response = await AppService.getVoicemails();
      handleJwksResponse(response, (data) => (voicemails = data || []));
    }
    let voicemailToBeDeleted;
    (broadcasts?.broadcastVoicemail || []).find((broadcast) => {
      const found = voicemails.find(
        (voicemail) => voicemail.id === broadcast.libraryId
      );
      voicemailToBeDeleted = found;
      return !!found;
    });
    return (
      voicemailToBeDeleted ||
      voicemails.sort((a, b) => {
        const valueA = +a.id;
        const valueB = +b.id;
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      })[0] ||
      {}
    );
  };

  const handleVoicemailsMaxLimitAlertSuccess = async (voicemailToBeDeleted) => {
    if (!voicemailToBeDeleted) {
      setState((prevState) => ({
        ...prevState,
        alert: {},
      }));
      return;
    }
    setState((prevState) => ({
      ...prevState,
      alert: {
        ...prevState.alert,
        isLoading: true,
      },
    }));
    const response = await Service.deleteVoicemail(voicemailToBeDeleted.id);
    handleJwksResponse(response, () => {});
    if (!response?.error) {
      state.immediateRelease
        ? handlePanelNavigate("next", 5)
        : handleSubmit(state.immediateRelease);
    }
    setState((prevState) => ({
      ...prevState,
      alert: {},
    }));
  };

  const addVoicemail = async (voicemail, recordingType) => {
    const formData = new FormData();
    if (voicemail.name) {
      //if (recordingType === 1) {
        formData.append("file", voicemail.file);
        formData.append("filename", voicemail.name);
      //} else {
        //formData.append("attachment", voicemail.file, voicemail.name);
      //}
    } else {
      formData.append("file", voicemail.file);
      formData.append("filename", voicemail.file.name);
    }

    let result = {};
    //const method = recordingType === 1 ? "addVoicemailInternal" : "addVoicemail";
    //const response = await Service[method](formData);
    const response = await Service.addVoicemailInternal(formData);
    if (
      Array.isArray(response?.error) &&
      response.error[0].errorCode === "CMN-423"
    ) {
      const voicemailToBeDeleted = await getOldestVoicemail();
      if (!userData?.voicemailDeletePreference) {
        setState((prevState) => ({
          ...prevState,
          alert: {
            ...prevState.alert,
            open: true,
            hasCancel: true,
            heading: "Are you sure?",
            positiveLabel: "Continue",
            content: (
              <>
                <Typography variant="body2">
                  You have reached the maximum uploads (100) on your account.
                  Proceeding will delete the oldest media on the account.
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {voicemailToBeDeleted?.filename}
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Please confirm before proceeding.
                </Typography>
              </>
            ),
            preferenceName: "voicemailDeletePreference",
            preference: false,
            onSuccess: () =>
              handleVoicemailsMaxLimitAlertSuccess(voicemailToBeDeleted),
          },
        }));
      } else {
        const deleteResponse = await Service.deleteVoicemail(
          voicemailToBeDeleted.id
        );
        handleJwksResponse(deleteResponse, () => {});
        if (!deleteResponse?.error) {
          return addVoicemail(voicemail, recordingType);
        }
      }
    } else {
      //handleJwksResponse(response, (data) => (result = data || {}));
      if (response?.error) {
        toast.error(
          Array.isArray(response?.error) ? response?.error[0].message : response?.error.message || response?.error
        );
      }
      result = response?.data?.fileUpload || {};
    }
    return result.id;
  };

  const handleSubmit = async (immediateSubmit = false) => {
    setState((prevState) => ({
      ...prevState,
      isSaving: true,
    }));
    let voicemailId = +state.recordingType === 2 ? state.callToRecordVoicemail?.id : state.selectedVoicemail.id;
    // if ([1, 2, 4].includes(+state.recordingType)) {
      if ([1, 4].includes(+state.recordingType)) {
      const files = {
        1: {
          file: state.recordedVoicemail,
          name: state.recordedVoicemailTitle + ".wav",
        },
        // 2: {
        //   file: state.callToRecordVoicemail?.messageContent,
        //   name: state.callToRecordVoicemailTitle + ".mp3",
        // },
        4: { file: state.uploadedVoicemail },
      };
      voicemailId = await addVoicemail(files[+state.recordingType], +state.recordingType);
      if (!voicemailId) {
        if (immediateSubmit) {
          handlePanelNavigate("back", state.panelIndex);
        }
        setState((prevState) => ({
          ...prevState,
          isSaving: false,
        }));
        return;
      }
    }

    let payload = {
      isAllRecipients: state.isAllRecipients,
      broadcastTitle: state.broadcastTitle,
      overrideCallerId: state.overrideCallerId,
      notifyRecipient: state.notifyRecipient,
      immediateRelease: state.immediateRelease,
      bcc: state.bbc.map((item) => item?.contact?.email),
      libraryId: voicemailId,
      includeSelfInBroadcast: state.includeSelfInBroadcast,
    };
    if (!state.isAllRecipients) {
      payload = {
        ...payload,
        recipientSites: state.recipientSites,
        recipientDepartments: state.recipientDepartments,
        recipientRoles: state.recipientRoles.map((item) => item.id),
        recipientSelectedContacts: state.recipientSelectedContacts.map(
          (item) => item.id
        ),
        recipients: state.recipients.map((item) => item.id),
      };
    }
    if (state.overrideCallerId) {
      payload = {
        ...payload,
        callerIdName: state.callerIdName,
        callerIdNumber: state.callerIdNumber?.phoneNumber,
      };
    }
    if (!state.immediateRelease) {
      payload = {
        ...payload,
        releaseDate: getZonedDate(),
      };
    }
    const serviceType = !!rowBeingEdited
      ? "updateBroadcast"
      : "createBroadcast";
    const { data, error } = await Service[serviceType](payload, state.id);
    if (error) {
      toast.error(
        Array.isArray(error) ? error[0].message : error.message || error
      );
      if (immediateSubmit) {
        handlePanelNavigate("back", state.panelIndex);
      }
    } else {
      toast.success(
        `Broadcast ${
          state.immediateRelease ? "released" : "scheduled"
        } successfully.`
      );
      if (immediateSubmit) {
        handlePanelNavigate("back", state.panelIndex);
        setState((prevState) => ({
          ...prevState,
          ...prepareFormData(data.broadcastVoicemail),
          broadcastComplete: immediateSubmit,
          recordedVoicemail: prevState.recordedVoicemail,
          callToRecordVoicemail: prevState.callToRecordVoicemail,
          uploadedVoicemail: prevState.uploadedVoicemail,
          recordingType: prevState.recordingType,
        }));
      } else {
        onClose(true);
      }
    }
    setState((prevState) => ({
      ...prevState,
      isSaving: false,
    }));
  };

  const getZonedDate = () => {
    const [hour, minute] = (state.releaseTime || "00:00").split(":");
    const timeZone = accountInfo?.regionalSettings?.timezone?.name;
    return zonedTimeToUtc(
      new Date(
        state.releaseYear,
        state.releaseMonth,
        state.releaseDay,
        (+hour === 12 ? 0 : +hour) + (state.releaseMeridiem === "PM" ? 12 : 0),
        +minute
      ),
      timeZone
    ).toISOString();
  };

  const handlePanelNavigate = (type = "next", currentPanelIndex = 0) => {
    if (currentPanelIndex === 5 && type === "next" && !state.immediateRelease) {
      handleSubmit();
      return;
    }
    const panelIndex =
      type === "next" ? ++currentPanelIndex : --currentPanelIndex;
    setState((prevState) => ({
      ...prevState,
      panelIndex,
    }));
  };

  const handleClose = (isSubmitted = false) => {
    onClose(isSubmitted);
  };

  const validateFields = (field, value) => {
    let errorMessage = "";
    const fieldValidatorMap = {
      broadcastTitle: [
        { type: VALIDATIONS.REQUIRED, value: true },
        { type: VALIDATIONS.MAX_LENGTH, value: 200 },
      ],
      callerIdName: [{ type: VALIDATIONS.REGEX, value: /^[a-zA-Z0-9\s#]*$/ }],
    };
    if (state.overrideCallerId) {
      fieldValidatorMap.callerIdName.push({
        type: VALIDATIONS.REQUIRED,
        value: true,
      });
      fieldValidatorMap.callerIdName.push({
        type: VALIDATIONS.MAX_LENGTH,
        value: 100,
      });
    }

    if (fieldValidatorMap[field]) {
      const validationResult = fieldValidatorMap[field].map((validation) =>
        validator(
          value,
          validation.type,
          validation.value,
          validation.inputType || "string"
        )
      );
      errorMessage = validationResult
        .filter((error) => !error?.isValid)
        .map((error) => error?.errorMessage)[0];
    } else {
      const errorFieldsMap = {
        0: ["broadcastTitle", "callerIdName"],
        3: ["callToRecordVoicemailNumber", "callToRecordVoicemailTitle"],
      };
      Object.keys(fieldValidatorMap).forEach((key) => {
        const message =
          (errorFieldsMap[state.panelIndex] || []).includes(key) &&
          validateFields(key, state[key]);
        if (!!message) {
          errorMessage = message;
        }
      });
    }

    return errorMessage;
  };

  const handleChange = (evt, type = "input") => {
    isFormChanged = true;
    const { name, value } =
      type === "input" || type === "select" || type === "radioGroup"
        ? evt.currentTarget || evt.target
        : evt;
    const errorMessage = validateFields(name, value) || " ";
    setState((prevState) => ({
      ...prevState,
      [name]: value,
      errors: {
        ...prevState.errors,
        [name]: errorMessage,
      },
    }));
  };

  const removeRecipient = (id) => {
    setState((prevState) => {
      const recipients = prevState.recipients.filter(
        (recipient) => recipient.id !== id
      );
      const recipientSites = prevState.recipientSites.filter((site) =>
        recipients.some((item) => item.site === site)
      );
      const recipientDepartments = prevState.recipientDepartments.filter(
        (department) =>
          recipients.some((item) => item.department === department)
      );
      const recipientRoles = prevState.recipientRoles.filter((role) =>
        recipients.some((item) => item.role === role.displayName)
      );
      const recipientSelectedContacts =
        prevState.recipientSelectedContacts.filter((contact) =>
          recipients.some((item) => item.id === contact.id)
        );
      return {
        ...prevState,
        recipients,
        recipientSites,
        recipientDepartments,
        recipientRoles,
        recipientSelectedContacts,
      };
    });
  };

  const removeBBC = (id) => {
    setState((prevState) => ({
      ...prevState,
      bbc: prevState.bbc.filter((item) => item.id !== id),
    }));
  };

  const getLoggedInCallerDetails = (overrideCallerId = false) => {
    if (overrideCallerId) {
      return {
        callerIdName: state.callerIdName,
        callerIdNumber: state.callerIdNumber?.phoneNumber,
      };
    }
    const loggedInUserExtensionId = !!rowBeingEdited
      ? rowBeingEdited?.createdBy?.extensionId
      : userData?.extensionId;
    const loggedInUserExtension =
      extensionList.find(
        (extension) => extension.id === +loggedInUserExtensionId
      ) || {};
    return {
      callerIdName: loggedInUserExtension.name,
      // callerIdNumber: loggedInUserExtension?.contact?.businessPhone,
      callerIdNumber: loggedInUserExtension?.extensionNumber,
    };
  };

  const getPanel = (panelIndex = 0) => {
    let component = <></>,
      title = "";
    switch (panelIndex) {
      case 0:
        component = (
          <BroadcastPanel
            broadcastTitle={state.broadcastTitle}
            callerIdName={state.callerIdName}
            callerIdNumber={state.callerIdNumber}
            overrideCallerId={state.overrideCallerId}
            phoneNumberList={phoneNumberList}
            errors={state.errors}
            handleChange={handleChange}
          />
        );
        title = "Start New Broadcast";
        break;
      case 1:
        component = (
          <RecipientsPanel
            isAllRecipients={state.isAllRecipients}
            recipientSites={state.recipientSites}
            siteList={siteList}
            recipientDepartments={state.recipientDepartments}
            departmentList={departmentList}
            recipientRoles={state.recipientRoles}
            userRoleList={userRoleList}
            recipientSelectedContacts={state.recipientSelectedContacts}
            individualContactList={individualContactList}
            recipients={state.recipients}
            notifyRecipient={state.notifyRecipient}
            roleAccess={roleAccess}
            handleChange={handleChange}
            removeRecipient={removeRecipient}
            updateRecipientList={updateRecipientList}
          />
        );
        title = "Add Recipients";
        break;
      case 2:
        component = (
          <ReleaseDatePanel
            immediateRelease={state.immediateRelease}
            releaseDay={state.releaseDay}
            releaseMonth={state.releaseMonth}
            releaseYear={state.releaseYear}
            releaseTime={state.releaseTime}
            releaseMeridiem={state.releaseMeridiem}
            timeZone={accountInfo?.regionalSettings?.timezone?.name}
            handleChange={handleChange}
          />
        );
        title = "Release Time & Date";
        break;
      case 3:
        component = (
          <AudioRecordPanel
            recordingType={state.recordingType}
            selectedVoicemail={state.selectedVoicemail}
            uploadedVoicemail={state.uploadedVoicemail}
            recordedVoicemail={state.recordedVoicemail}
            recordedVoicemailTitle={state.recordedVoicemailTitle}
            callToRecordVoicemail={state.callToRecordVoicemail}
            callToRecordVoicemailNumber={state.callToRecordVoicemailNumber}
            callToRecordVoicemailTitle={state.callToRecordVoicemailTitle}
            roleAccess={roleAccess}
            phoneNumberList={phoneNumberList}
            voicemailList={voicemailList.sort((a, b) =>
              a.filename.localeCompare(b.filename)
            )}
            countryList={countryList}
            handleChange={handleChange}
            handleJwksResponse={handleJwksResponse}
          />
        );
        title = "Audio Recording";
        break;
      case 4:
        component = (
          <EmailUpdatesPanel
            bbc={state.bbc}
            individualContactList={individualContactList}
            handleChange={handleChange}
            removeBBC={removeBBC}
          />
        );
        title = "Email Updates";
        break;
      case 5:
        component = (
          <ConfirmDetailsPanel
            broadcastTitle={state.broadcastTitle}
            recordingType={state.recordingType}
            recordedVoicemail={state.recordedVoicemail}
            callToRecordVoicemail={state.callToRecordVoicemail}
            selectedVoicemail={state.selectedVoicemail}
            uploadedVoicemail={state.uploadedVoicemail}
            recipients={state.recipients}
            callerDetails={getLoggedInCallerDetails(state.overrideCallerId)}
            immediateRelease={state.immediateRelease}
            releaseDate={getZonedDate()}
            notifyRecipient={state.notifyRecipient}
            bbc={state.bbc}
            includeSelfInBroadcast={state.includeSelfInBroadcast}
            timezone={accountInfo?.regionalSettings?.timezone?.name}
            handleChange={handleChange}
            showDetails={showDetails}
            isNewForm={!rowBeingEdited}
            isFormChanged={isFormChanged}
            broadcastComplete={state.broadcastComplete}
            broadcastErrors={state.broadcastErrors}
            isAllRecipients={state.isAllRecipients}
          />
        );
        title = showDetails
          ? "Broadcast Details"
          : state.broadcastComplete
          ? "Broadcast Complete"
          : "Confirm Details";
        break;
      case 6:
        component = <ProgressPanel handleSubmit={handleSubmit} />;
        title = "Sending";
        break;
    }
    return {
      title,
      component,
    };
  };
  const validate = (panelIndex) => {
    let isValid = true;
    switch (panelIndex) {
      case 0:
        isValid =
          !!state.broadcastTitle &&
          (state.overrideCallerId
            ? state.callerIdName && state.callerIdNumber
            : true);
        break;
      case 1:
        isValid = !!state.recipients.length || state.isAllRecipients;
        break;
      case 2:
        isValid = state.immediateRelease
          ? true
          : state.releaseDay &&
            typeof state.releaseMonth === "number" &&
            state.releaseYear &&
            state.releaseTime;
        break;
      case 3:
        isValid =
          (+state.recordingType === 1 &&
            state.recordedVoicemail &&
            state.recordedVoicemailTitle) ||
          (+state.recordingType === 2 && state.callToRecordVoicemail?.contentUri) ||
          (+state.recordingType === 3 &&
            Object.keys(state.selectedVoicemail || {}).length) ||
          (+state.recordingType === 4 && state.uploadedVoicemail);
        break;
    }
    return isValid;
  };

  const getReleaseDateEntries = (releaseDate, timezone) => {
    let releaseDateEntries = {
      releaseDay: "",
      releaseMonth: null,
      releaseYear: "",
      releaseTime: "",
      releaseMeridiem: "AM",
    };
    if (!releaseDate) {
      return releaseDateEntries;
    }
    const date = utcToZonedTime(releaseDate, timezone);
    let parsedHours = date.getHours();
    parsedHours = parsedHours > 12 ? parsedHours - 12 : parsedHours;
    const pad = (num, size) => {
      num = num.toString();
      while (num.length < size) num = "0" + num;
      return num;
    };
    return (releaseDateEntries = {
      releaseDay: String(date.getDate()),
      releaseMonth: date.getMonth(),
      releaseYear: String(date.getFullYear()),
      releaseTime: `${pad(parsedHours, 2)}:${pad(date.getMinutes(), 2)}`,
      releaseMeridiem: date.getHours() >= 12 ? "PM" : "AM",
    });
  };

  const extensionExistInRecipientList = (
    extensions = [],
    skipRecipient = ""
  ) => {
    return extensions.filter((extension) => {
      const isSites =
        !state.recipientSites.includes(extension.site) ||
        skipRecipient === "SITES";
      const isDepartments =
        !state.recipientDepartments.includes(extension.department) ||
        skipRecipient === "DEPARTMENTS";
      const isRoles =
        !state.recipientRoles.some(
          (role) => role.displayName === extension.role
        ) || skipRecipient === "ROLES";
      const isIndividualContacts =
        !state.recipientSelectedContacts.some(
          (item) => item.id === extension.id
        ) || skipRecipient === "INDIVIDIUAL_CONTACTS";
      return isSites && isDepartments && isRoles && isIndividualContacts;
    });
  };

  const updateRecipientList = (type, action, value = []) => {
    let extensions = [];
    extensionList.map((extension) => {
      const recipientExtesnion = {
        id: extension.id,
        name: extension.name,
        email: extension.contact?.email,
        department: extension.contact?.department,
        site: extension.site?.name,
        role: extension.roles && extension.roles[0]?.displayName,
      };
      switch (type) {
        case "SITES":
          if (value.includes(extension?.site?.name)) {
            extensions.push(recipientExtesnion);
          }
          break;
        case "DEPARTMENTS":
          if (value.includes(extension?.contact?.department)) {
            extensions.push(recipientExtesnion);
          }
          break;
        case "ROLES":
          if (value.some((item) => item.id == extension?.roles[0]?.id)) {
            extensions.push(recipientExtesnion);
          }
          break;
        case "INDIVIDIUAL_CONTACTS":
          if (value.some((item) => item.id === extension?.id)) {
            extensions.push(recipientExtesnion);
          }
          break;
      }
    });

    if (action !== "selectOption") {
      extensions = extensionExistInRecipientList(extensions, type);
    }

    setState((prevState) => {
      const recipients = extensions.reduce(
        (acc, extension) => {
          const found = acc.some((item) => item.id === extension.id);
          if (action === "selectOption") {
            !found && acc.push(extension);
          } else {
            acc = acc.filter((item) => item.id !== extension.id);
          }
          return acc;
        },
        [...prevState.recipients]
      );
      return {
        ...prevState,
        recipients,
      };
    });
  };
  const prepareFormData = (rowBeingEdited = {}) => {
    return {
      id: rowBeingEdited.id,
      broadcastTitle: rowBeingEdited.broadcastTitle,
      overrideCallerId: rowBeingEdited.overrideCallerId,
      callerIdName: rowBeingEdited.callerIdName || "",
      callerIdNumber: phoneNumberList.find(
        (item) => item.phoneNumber === rowBeingEdited.callerIdNumber
      ),
      isAllRecipients: rowBeingEdited.isAllRecipients,
      notifyRecipient: rowBeingEdited.notifyRecipient,
      recipientSites: rowBeingEdited.recipientSites || [],
      recipientDepartments: rowBeingEdited.recipientDepartments || [],
      recipientRoles: (rowBeingEdited.recipientRoles || []).map(
        (recipientRoleId) =>
          userRoleList.find((item) => item.id === recipientRoleId) || {}
      ),
      includeSelfInBroadcast: rowBeingEdited?.includeSelfInBroadcast,
      recipientSelectedContacts: (
        rowBeingEdited.recipientSelectedContacts || []
      ).map(
        (item) =>
          extensionList.find((extension) => extension.id === +item) || {}
      ),

      recipients: (rowBeingEdited.recipients || []).map((item) => {
        const extension =
          extensionList.find((extension) => extension.id == item) || {};
        return {
          id: extension.id,
          name: extension.name,
          email: extension.contact?.email,
          department: extension.contact?.department,
          site: extension.site?.name,
          role: extension.roles && extension.roles[0]?.displayName,
        };
      }),
      immediateRelease: rowBeingEdited.immediateRelease,
      ...getReleaseDateEntries(
        // rowBeingEdited.immediateRelease,
        rowBeingEdited.releaseDate,
        accountInfo?.regionalSettings?.timezone?.name
      ),
      recordingType: 3,
      selectedVoicemail: voicemailList.find(
        (item) => item.id === rowBeingEdited.libraryId
      ),
      bbc: rowBeingEdited.bcc.map((item) => {
        return (
          extensionList.find(
            (extension) => extension?.contact?.email === item
          ) || {}
        );
      }),
      broadcastErrors: rowBeingEdited?.errors || [],
    };
  };


  
  /*
  useEffect(() => {
    if (
      appData?.defaultCallerId?.default_caller_id_name &&
      appData?.defaultCallerId?.default_caller_id_number
    ) {
      setState((prevState) => ({
        ...prevState,
        overrideCallerId: !!rowBeingEdited ? prevState.overrideCallerId : true,
        callerIdName: appData?.defaultCallerId?.default_caller_id_name,
        callerIdNumber:
          (phoneNumberList || []).find(
            (item) =>
              item.phoneNumber ===
              appData?.defaultCallerId?.default_caller_id_number
          ) || {},
      }));
    }
  }, [appData?.defaultCallerId, phoneNumberList]);
  */

  useEffect(() => {
    if (rowBeingEdited) {
      const data = prepareFormData(rowBeingEdited);
      if (!(data.callerIdName && data.callerIdNumber)) {
        data.callerIdName = appData?.defaultCallerId?.default_caller_id_name;
        data.callerIdNumber =
          (phoneNumberList || []).find(
            (item) =>
              item.phoneNumber ===
              appData?.defaultCallerId?.default_caller_id_number
          ) || {};
      }
      setState((prevState) => ({
        ...prevState,
        ...data,
      }));
    }
    return () => (isFormChanged = false);
  }, [rowBeingEdited]);

  return (
    <>
      <CreateBroadcastView
        panelIndex={state.panelIndex}
        panel={getPanel(state.panelIndex)}
        immediateRelease={state.immediateRelease}
        isSaving={state.isSaving}
        isEditing={!!rowBeingEdited}
        showDetails={showDetails}
        isScheduled={rowBeingEdited?.status === "SCHEDULED"}
        roleAccess={roleAccess}
        handlePanelNavigate={handlePanelNavigate}
        handleClose={handleClose}
        validate={validate}
        validateFields={validateFields}
        userData={userData}
        isCancelling={state.isCancelling}
        broadcastComplete={state.broadcastComplete}
        handleConfirmation={(value) =>
          setState((prevState) => ({
            ...prevState,
            isCancelling: value,
          }))
        }
        handleDelete={() => {
          handleDelete([rowBeingEdited.id]);
          setState((prevState) => ({
            ...prevState,
            isCancelling: false,
          }));
          onClose();
        }}
        handleSubmit={handleSubmit}
      />
      {state.alert.open && (
        <Alert
          open={state.alert.open}
          heading={state.alert.heading}
          content={state.alert.content}
          positiveLabel={state.alert.positiveLabel}
          negativeLabel={state.alert.negativeLabel}
          isLoading={state.alert.isLoading}
          hasCancel={state.alert.hasCancel}
          preferenceName={state.alert.preferenceName}
          preference={state.alert.preference}
          onCancel={() => {
            setState((prevState) => ({
              ...prevState,
              alert: {},
            }));
          }}
          onSuccess={state.alert.onSuccess}
          handlePreference={patchPreference}
        />
      )}
    </>
  );
};

export default CreateBroadcast;
